/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { FormattedMessage } from "react-intl";
import { NextApiRequest } from "next";
import useStore, { BlockType, Company } from "store";

import JoinNewsletter from "components/Sections/JoinNewsletter";
import BaseContainer from "components/Containers/BaseContainer";
import Text from "components/Text";
import ContactForm from "components/Sections/ContactForm";
import dynamic from "next/dynamic";
import messages from "store/messages";
import API_URL from "../constants";

const Toaster = dynamic(() => import("react-hot-toast").then((module) => module.Toaster), {
  ssr: false,
});

export type InitialContactObject = {
  name: string;
  email: string;
  text: string;
};

type Props = {
  blocks:BlockType[]
  company: Company
  token: string
  domain: string
}

export default function ContactPage(props: Props) {
  const { blocks, company, token, domain } = props;
  const { setCompany, setDomain } = useStore();

  useEffect(() => {
    setCompany(company);
    setDomain(domain);
  }, [company, domain, setCompany, setDomain]);

  const Body = useMemo(() => styled.div`
    display: grid;
    width: 80%;
    gap: 3rem;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    flex-shrink: 0;
    @media (min-width: 640px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  `, []);

  const LeftPart = useMemo(() => styled.div`
    display: flex;
    flex-direction: column;
    max-width: 24rem;
    gap: 30px;
  `, []);

  const LeftPartItem = useMemo(() => styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
  `, []);

  return (
    <>
      <Toaster />
      <BaseContainer centered direction="column" withTopMargin>
        <Text tag="h1" variant="h1">
          <FormattedMessage {...messages.contactTitle} />
        </Text>
        <Body>
          <LeftPart>
            <LeftPartItem>
              <Text tag="p" variant="boldExtraSmall600">
                🗺️ <FormattedMessage {...messages.addressTitle} />
              </Text>
              <Text tag="span" variant="normal">
                {company.address}
              </Text>
            </LeftPartItem>
            <LeftPartItem>
              <Text tag="p" variant="boldExtraSmall600">
                💌 <FormattedMessage {...messages.emailTitle} />
              </Text>
              <Text tag="span" variant="normal">
                {company.email}
              </Text>
            </LeftPartItem>
            <LeftPartItem>
              <Text tag="p" variant="boldExtraSmall600">
                ☎️ <FormattedMessage {...messages.phoneTitle} />
              </Text>
              <Text tag="span" variant="normal">
                {company.phone}
              </Text>
            </LeftPartItem>
          </LeftPart>
          <ContactForm token={token} />
        </Body>
        {blocks.map((block, index) => {
          let component = null;
          if (block.type === "NEWSLETTER") {
            const indexKey = `${index}-${block.type}`;
            component = <JoinNewsletter block={block} key={indexKey} />;
          }
          return component;
        })}
      </BaseContainer>
    </>
  );
}

export async function getServerSideProps(context: NextApiRequest) {
  const { locale } = context.query;
  
  // @ts-ignore
  const domain = context?.req.headers.host;

  const response = await fetch(`${API_URL}/api/public/get-site-by-id`, {
    method: "POST",
    body: JSON.stringify({
      domain,
      locale,
      page: "CONTACT",
    }),
  });
  
  const {
    blocks, company, seo, headCode = "", metaTags, scripts,
    locale: siteLocale, token, widgetId, currencySymbol,
  } = await response.json();

  return {
    props: {
      blocks,
      company,
      locale: siteLocale,
      seo,
      token,
      headCode,
      domain,
      widgetId,
      currencySymbol,
      metaTags,
      scripts,
    },
  };
}
